<template>
  <v-card
    class="frontpage-box-card"
    flat
    rounded="lg"
    >
    <v-card-text>
      <div class="box-title">
        {{ title }}
      </div>
      <div class="box-amount">
        <span class="amount">{{ currency(amount) }}</span>
        <span class="unit">kr.</span>
      </div>
      <div
        v-if="subtitle"
        class="box-subtitle"
        >
        {{ subtitle }}
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { useCurrency } from '@/composables/useCurrency'
const { currency } = useCurrency()
defineProps<{
  title: string
  subtitle?: string
  amount: string | number
}>()
</script>

<style scoped lang="scss">
@use "@/scss/settings/variables" as var;
@use "@/scss/settings/mixins" as mixins;

.frontpage-box-card {
  height: 100%;
  padding: 24px;
  background-color: var.$color-background;
  border: 1px solid var.$color-border !important;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  font-family: var.$font-family-base;
  border-radius: var.$border-radius-lg;
  box-shadow: none !important;
  @include mixins.text-body;

  /* NEW: smooth hover/focus transitions */
  transition: border-color .15s ease, box-shadow .15s ease, transform .15s ease !important;

  /* Hover: slightly darker border & a whisper of elevation */
  &:hover {
    border-color: color-mix(in srgb, var.$color-border 70%, #000 30%) !important;
    box-shadow: 0 1px 0 rgba(0,0,0,.02), 0 0 0 3px rgba(0,0,0,.02) inset !important;
  }

  /* Keyboard focus */
  &:focus-visible {
    outline: none;
    border-color: color-mix(in srgb, var.$color-border 50%, #00877C 50%);
    box-shadow: 0 0 0 3px rgba(0, 135, 124, .12);
  }

  :global(a) & { cursor: pointer; }

  // Dark mode
  :global(body.dark-mode) & {
    background-color: var.$color-card-dark;
    border-color: var.$color-border-dark !important;
    color: var.$color-text-dark;

    &:hover {
      border-color: color-mix(in srgb, var.$color-border-dark 70%, #fff 30%) !important;
      box-shadow: 0 1px 0 rgba(255,255,255,.03), 0 0 0 3px rgba(255,255,255,.03) inset !important;
    }

    &:focus-visible {
      border-color: color-mix(in srgb, var.$color-border-dark 50%, #00d0c0 50%);
      box-shadow: 0 0 0 3px rgba(0, 208, 192, .16);
    }
  }
}

.box-title {
  font-family: var.$font-family-base !important;
  font-size: var.$font-size-base;
  font-weight: var.$font-weight-medium !important;
  color: var.$color-text;
  margin-bottom: 16px;
  @include mixins.text-heading;
  :global(body.dark-mode) & { color: var.$color-text-dark; }
}

.box-amount {
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-size: 2.2rem;
  font-weight: var.$font-weight-medium;
  color: var.$color-text;
  letter-spacing: 1px;
  @include mixins.text-heading;
  :global(body.dark-mode) & { color: #fff; }
  .unit {
    font-size: 1.1rem;
    font-weight: var.$font-weight-regular;
    color: var.$color-text;
    margin-left: 4px;
    :global(body.dark-mode) & { color: var.$color-text-dark; }
  }
}

.box-subtitle {
  margin-top: 12px;
  font-size: 0.85rem;
  color: var.$color-text-muted;
  @include mixins.text-caption;
  :global(body.dark-mode) & { color: var.$color-text-dark; }
}

@media (prefers-reduced-motion: reduce) {
  .frontpage-box-card { transition: border-color .15s ease, box-shadow .15s ease; }
}
</style>